import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { TrustBanner, TrustBadges } from '../components/TrustSignals';
import { StripeCheckoutModal } from '../components/StripeCheckout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, MapPin, Clock, Star, ChefHat, Utensils, Coffee, 
  Package, Plus, Minus, ShoppingCart, Truck, ArrowLeft,
  Loader2, Phone, X, Check, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  appetizers: Utensils,
  mains: ChefHat,
  sides: Package,
  drinks: Coffee,
  desserts: Star,
  specials: Sparkles,
};

// Restaurant List Page
export function FoodDeliveryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('all');

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API}/food/restaurants`);
      setRestaurants(response.data.restaurants || []);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const cuisineTypes = ['all', ...new Set(restaurants.flatMap(r => r.cuisine_type || []))];

  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.cuisine_type?.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCuisine = selectedCuisine === 'all' || r.cuisine_type?.includes(selectedCuisine);
    return matchesSearch && matchesCuisine;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50" data-testid="food-delivery-page">
      <Header />
      
      {/* Modern Hero Section */}
      <div className="hero-gradient-food relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 noise-overlay opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium">Fort Wayne's Fastest Delivery</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              Craving Something <span className="text-yellow-300">Delicious?</span>
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto mb-8">
              Order from the best local restaurants and get it delivered hot to your door
            </p>
            
            {/* Modern Search */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400 z-10" />
              <input
                type="text"
                placeholder="Search restaurants, cuisines, or dishes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-modern w-full"
                data-testid="food-search"
              />
            </div>
          </div>
        </div>
        
        {/* Wave decoration */}
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 100" fill="none">
          <path d="M0 100V50C240 16 480 0 720 0C960 0 1200 16 1440 50V100H0Z" fill="white" fillOpacity="0.1"/>
          <path d="M0 100V60C240 26 480 10 720 10C960 10 1200 26 1440 60V100H0Z" className="fill-orange-50"/>
        </svg>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4">
        {/* Cuisine Filter Chips */}
        <div className="flex flex-wrap gap-3 justify-center mb-8 animate-fade-in-up stagger-2">
          {cuisineTypes.slice(0, 10).map(cuisine => (
            <button
              key={cuisine}
              onClick={() => setSelectedCuisine(cuisine)}
              className={`chip-modern ${selectedCuisine === cuisine ? 'active' : ''}`}
            >
              {cuisine === 'all' ? (
                <><Utensils className="w-4 h-4" /> All</>
              ) : (
                cuisine
              )}
            </button>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap justify-center gap-8 mb-8 text-center">
          <div className="animate-fade-in-up stagger-1">
            <p className="text-3xl font-bold text-orange-600">{restaurants.length}+</p>
            <p className="text-sm text-gray-500">Restaurants</p>
          </div>
          <div className="animate-fade-in-up stagger-2">
            <p className="text-3xl font-bold text-orange-600">15-30</p>
            <p className="text-sm text-gray-500">Min Delivery</p>
          </div>
          <div className="animate-fade-in-up stagger-3">
            <p className="text-3xl font-bold text-orange-600">$0</p>
            <p className="text-sm text-gray-500">Delivery Fee*</p>
          </div>
        </div>

        {/* Restaurant Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="card-modern p-4">
                <div className="skeleton-modern h-48 mb-4" />
                <div className="skeleton-modern h-6 w-3/4 mb-2" />
                <div className="skeleton-modern h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="card-glass text-center py-16 px-8 animate-fade-in-up">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center">
              <ChefHat className="w-12 h-12 text-orange-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No Restaurants Found</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {searchTerm ? `No results for "${searchTerm}"` : 'Be the first restaurant to join our platform!'}
            </p>
            {user && (
              <Button 
                onClick={() => navigate('/food/apply')}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-3 rounded-full"
              >
                Register Your Restaurant
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRestaurants.map((restaurant, index) => (
              <div 
                key={restaurant.provider_id}
                className="card-modern cursor-pointer animate-fade-in-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/food/restaurant/${restaurant.provider_id}`)}
                data-testid={`restaurant-card-${restaurant.provider_id}`}
              >
                <div className="product-image-modern">
                  {restaurant.banner_url ? (
                    <img 
                      src={restaurant.banner_url} 
                      alt={restaurant.business_name}
                    />
                  ) : (
                    <img 
                      src={[
                        'https://images.unsplash.com/photo-1737837479225-7af10798403b?w=600&fit=crop',
                        'https://images.pexels.com/photos/10480246/pexels-photo-10480246.jpeg?auto=compress&cs=tinysrgb&w=600',
                        'https://images.unsplash.com/photo-1625937343774-264e25891331?w=600&fit=crop',
                        'https://images.pexels.com/photos/5893636/pexels-photo-5893636.jpeg?auto=compress&cs=tinysrgb&w=600',
                        'https://images.unsplash.com/photo-1708459648641-00157553ece6?w=600&fit=crop',
                      ][index % 5]}
                      alt={restaurant.business_name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {/* Floating rating badge */}
                  <div className="absolute top-4 right-4 rating-modern">
                    <Star className="w-4 h-4 text-yellow-600 fill-yellow-500" />
                    <span>{restaurant.rating?.toFixed(1) || '5.0'}</span>
                  </div>
                  {/* Delivery time badge */}
                  <div className="absolute bottom-4 left-4 delivery-badge">
                    <Clock className="w-4 h-4" />
                    <span>20-35 min</span>
                  </div>
                </div>
                
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    {restaurant.logo_url && (
                      <div className="store-logo-modern shrink-0">
                        <img src={restaurant.logo_url} alt="" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-bold text-lg text-gray-900 group-hover:text-orange-600 transition-colors truncate">
                        {restaurant.business_name}
                      </h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(restaurant.cuisine_type || []).slice(0, 3).map(cuisine => (
                          <span key={cuisine} className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {cuisine}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{restaurant.address?.slice(0, 25)}...</span>
                    </div>
                    <div className="text-sm font-medium text-green-600">
                      Free Delivery
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// Restaurant Detail & Order Page
export function RestaurantDetailPage() {
  const { providerId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuByCategory, setMenuByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState({
    phone: '',
    delivery_address: '',
    delivery_instructions: '',
    tip: 0,
    order_type: 'delivery'
  });
  const [showPayment, setShowPayment] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  useEffect(() => {
    fetchRestaurantDetail();
  }, [providerId]);

  const fetchRestaurantDetail = async () => {
    try {
      const response = await axios.get(`${API}/food/restaurant/${providerId}`);
      setRestaurant(response.data.restaurant);
      setMenuItems(response.data.menu_items || []);
      setMenuByCategory(response.data.menu || {});
    } catch (error) {
      toast.error('Failed to load restaurant');
      navigate('/food');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.item_id);
    if (existing) {
      setCart(cart.map(c => 
        c.item_id === item.item_id 
          ? {...c, quantity: c.quantity + 1}
          : c
      ));
    } else {
      setCart([...cart, {...item, quantity: 1}]);
    }
    toast.success(`Added ${item.name} to cart`);
  };

  const updateCartQuantity = (itemId, delta) => {
    setCart(cart.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? {...item, quantity: newQty} : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.item_id !== itemId));
  };

  const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryFee = checkoutData.order_type === 'delivery' ? (restaurant?.delivery_fee || 4.99) : 0;
  const tax = cartSubtotal * 0.07;
  const cartTotal = cartSubtotal + deliveryFee + tax + checkoutData.tip;

  const handlePlaceOrder = async () => {
    if (!user) {
      toast.error('Please login to place an order');
      navigate('/login');
      return;
    }

    if (cartSubtotal < (restaurant?.minimum_order || 0)) {
      toast.error(`Minimum order is $${restaurant?.minimum_order?.toFixed(2)}`);
      return;
    }

    try {
      const response = await axios.post(`${API}/food/order`, {
        provider_id: providerId,
        restaurant_name: restaurant?.business_name,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        phone: checkoutData.phone,
        delivery_address: checkoutData.delivery_address,
        delivery_instructions: checkoutData.delivery_instructions,
        tip: checkoutData.tip,
        order_type: checkoutData.order_type,
        delivery_fee: deliveryFee,
        estimated_delivery_time: restaurant?.estimated_delivery_time || 30
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      setCurrentOrderId(response.data.order_id);
      setShowCheckout(false);
      setShowPayment(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setCart([]);
    toast.success('Order placed successfully! Your food is being prepared.');
    navigate('/food/orders');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="restaurant-detail-page">
      <Header />
      
      {/* Restaurant Banner */}
      <div className="relative h-48 md:h-64 bg-gradient-to-r from-orange-400 to-red-500">
        {restaurant?.banner_url && (
          <img 
            src={restaurant.banner_url} 
            alt={restaurant.business_name}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <Button 
            variant="ghost" 
            className="mb-4 text-white hover:bg-white/20"
            onClick={() => navigate('/food')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Restaurants
          </Button>
          <h1 className="text-3xl font-bold">{restaurant?.business_name}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span>{restaurant?.rating?.toFixed(1) || '5.0'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-5 h-5" />
              <span>{restaurant?.estimated_delivery_time} min</span>
            </div>
            <div className="flex items-center gap-1">
              <Truck className="w-5 h-5" />
              <span>${restaurant?.delivery_fee?.toFixed(2)} delivery</span>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Menu */}
          <div className="lg:col-span-2">
            {Object.entries(menuByCategory).map(([category, items]) => (
              <div key={category} className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2 capitalize">
                  {CATEGORY_ICONS[category] && <>{(() => { const Icon = CATEGORY_ICONS[category]; return <Icon className="w-5 h-5" />; })()}</>}
                  {category}
                </h2>
                <div className="grid gap-4">
                  {items.map(item => (
                    <Card key={item.item_id} className="overflow-hidden">
                      <CardContent className="p-4 flex gap-4">
                        {item.image_url ? (
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Utensils className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-gray-900">{item.name}</h3>
                              <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
                            </div>
                            <p className="font-bold text-green-600">${item.price?.toFixed(2)}</p>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <Clock className="w-3 h-3" />
                              {item.prep_time} min
                              {item.ai_price_enabled && (
                                <Badge className="bg-purple-100 text-purple-600 text-xs">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Dynamic Price
                                </Badge>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => addToCart(item)}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Cart Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Your Order
                  {cart.length > 0 && (
                    <Badge>{cart.reduce((sum, i) => sum + i.quantity, 0)}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Your cart is empty</p>
                ) : (
                  <>
                    <div className="space-y-3 mb-4">
                      {cart.map(item => (
                        <div key={item.item_id} className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.name}</p>
                            <p className="text-xs text-gray-500">${item.price?.toFixed(2)} each</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => updateCartQuantity(item.item_id, -1)}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => updateCartQuantity(item.item_id, 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-500"
                              onClick={() => removeFromCart(item.item_id)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Subtotal</span>
                        <span>${cartSubtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Delivery</span>
                        <span>${deliveryFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tax</span>
                        <span>${tax.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Total</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full mt-4 bg-green-600 hover:bg-green-700"
                      onClick={() => setShowCheckout(true)}
                    >
                      Checkout
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Checkout</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Order Type</label>
                <div className="flex gap-2 mt-1">
                  <Button
                    variant={checkoutData.order_type === 'delivery' ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setCheckoutData({...checkoutData, order_type: 'delivery'})}
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    Delivery
                  </Button>
                  <Button
                    variant={checkoutData.order_type === 'pickup' ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setCheckoutData({...checkoutData, order_type: 'pickup'})}
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Pickup
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Phone Number</label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={checkoutData.phone}
                  onChange={(e) => setCheckoutData({...checkoutData, phone: e.target.value})}
                />
              </div>

              {checkoutData.order_type === 'delivery' && (
                <>
                  <div>
                    <label className="text-sm font-medium">Delivery Address</label>
                    <Textarea
                      placeholder="Enter your full address"
                      value={checkoutData.delivery_address}
                      onChange={(e) => setCheckoutData({...checkoutData, delivery_address: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Delivery Instructions (optional)</label>
                    <Input
                      placeholder="e.g., Ring doorbell, leave at door"
                      value={checkoutData.delivery_instructions}
                      onChange={(e) => setCheckoutData({...checkoutData, delivery_instructions: e.target.value})}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="text-sm font-medium">Add a Tip</label>
                <div className="flex gap-2 mt-1">
                  {[0, 2, 3, 5].map(tip => (
                    <Button
                      key={tip}
                      variant={checkoutData.tip === tip ? 'default' : 'outline'}
                      className="flex-1"
                      onClick={() => setCheckoutData({...checkoutData, tip})}
                    >
                      {tip === 0 ? 'None' : `$${tip}`}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>${cartSubtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery</span>
                  <span>${deliveryFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                {checkoutData.tip > 0 && (
                  <div className="flex justify-between">
                    <span>Tip</span>
                    <span>${checkoutData.tip.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowCheckout(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600"
                  onClick={handlePlaceOrder}
                  disabled={!checkoutData.phone || (checkoutData.order_type === 'delivery' && !checkoutData.delivery_address)}
                >
                  Place Order
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stripe Payment Modal */}
      <StripeCheckoutModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        amount={cartTotal}
        orderId={currentOrderId}
        itemName={`Order from ${restaurant?.business_name}`}
        onSuccess={handlePaymentSuccess}
        paymentType="marketplace"
      />
    </div>
  );
}

export default FoodDeliveryPage;
